import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/App';

import { logger } from '@/utils/logger';
import Footer from '@/components/Footer';
import { ArrowLeft } from 'lucide-react';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

export default function CookiePolicy() {
  const navigate = useNavigate();
  const [policy, setPolicy] = useState('');
  const [lastUpdated, setLastUpdated] = useState('');
  const [loading, setLoading] = useState(true);

  useDocumentMeta({
    title: 'Cookie Policy, Mementa',
    description: 'How Mementa uses cookies and how you can manage them.',
    url: 'https://mementa.gifts/cookies',
  });

  useEffect(() => {
    const fetchPolicy = async () => {
      try {
        const response = await api.get('/cookie-policy');
        setPolicy(response.data.content || '');
        setLastUpdated(response.data.last_updated || '');
      } catch (error) {
        logger.error('Error fetching cookie policy:', error);
        setPolicy('Unable to load the Cookie Policy. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    fetchPolicy();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFF8F0] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#9E6752] mx-auto mb-4"></div>
          <p style={{ color: '#73766A' }}>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFF8F0] page-enter">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <button
          onClick={() => navigate('/browse')}
          className="flex items-center gap-2 mb-6 px-4 py-2 rounded-full transition-all text-[#9E6752] hover:bg-[#9E6752] hover:!text-white border-2"
          style={{ borderColor: '#9E6752' }}
          data-testid="cookie-policy-back-button"
        >
          <ArrowLeft size={20} />
          <span className="font-medium">Back to Browse</span>
        </button>

        <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Playfair Display, serif', color: '#20212B' }}>
          Cookie Policy
        </h1>
        {lastUpdated && (
          <p className="text-sm text-gray-500 mb-8">
            Last Updated: {new Date(lastUpdated).toLocaleDateString()}
          </p>
        )}

        <div className="bg-white rounded-xl p-8 shadow-sm" data-testid="cookie-policy-content">
          <div className="prose prose-gray max-w-none" style={{ color: '#534145' }}>
            <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
              {policy}
            </pre>
          </div>
        </div>

        <div className="mt-6 text-center">
          <button
            onClick={() => window.dispatchEvent(new CustomEvent('mementa:open-cookie-prefs'))}
            className="px-6 py-2.5 rounded-full text-sm font-semibold text-white transition-transform hover:scale-105"
            style={{ background: '#9E6752' }}
            data-testid="cookie-policy-manage-btn"
          >
            Manage Cookie Preferences
          </button>
        </div>
      </div>
      <Footer />
    </div>
  );
}
